import { useCallback, useMemo } from "react";
import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import { ReactNode } from "react-markdown";

import { Action } from "constants/events";
import { useStrainPageContext } from "context/StrainPageContext";
import { StrainAttribute, Terp } from "custom-types/Strain";
import { useEventTracker } from "hooks/useEventTracker";
import { extractStrainSensations } from "utils/extractStrainSensations";

import PlusStylizedIcon from "components/Icons/plus_stylized.svg";
import TerpenesFlask from "components/Icons/TerpenesFlask";
import ThumbDownStylizedIcon from "components/Icons/thumb_down_stylized.svg";
import ThumbUpStylizedIcon from "components/Icons/thumb_up_stylized.svg";

import StrainAttributes from "./StrainAttributes";

const StrainHighlights = ({
  showAllHighlights = true,
  isStrainPage,
}: {
  showAllHighlights?: boolean;
  isStrainPage?: boolean;
}) => {
  const { strain } = useStrainPageContext();

  const { helpsWith, negatives, topThreeTerps, feelings } = useMemo(() => {
    return {
      feelings: extractStrainSensations(strain.effects).slice(0, 3),
      helpsWith: extractStrainSensations(
        {
          ...strain.symptoms,
          ...strain.conditions,
        },
        "votes",
      ).slice(0, 3),
      negatives: strain.negatives
        ? extractStrainSensations(strain.negatives).slice(0, 3)
        : [],
      topThreeTerps: Object.values(strain.terps)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3),
    };
  }, [strain]);

  const highlightsAvailable =
    (!isEmpty(strain) && helpsWith && showAllHighlights) ||
    feelings ||
    negatives ||
    topThreeTerps;

  return (
    highlightsAvailable && (
      <div className="flex flex-col gap-sm border border-light-grey rounded text-xs p-lg">
        <div className="uppercase font-bold">STRAIN HIGHLIGHTS</div>
        <ul className="flex flex-col gap-sm">
          {negatives.length > 0 && (
            <StrainHighlight
              title="Negatives"
              hightlights={negatives}
              icon={() => <ThumbDownStylizedIcon />}
              strainId={strain.id}
              isStrainPage={isStrainPage}
            />
          )}
          {feelings.length > 0 && (
            <StrainHighlight
              title="Feelings"
              hightlights={feelings}
              icon={() => <ThumbUpStylizedIcon />}
              strainId={strain.id}
              isStrainPage={isStrainPage}
            />
          )}
          {showAllHighlights && helpsWith.length > 0 && (
            <StrainHighlight
              title="Helps with"
              hightlights={helpsWith}
              icon={() => <PlusStylizedIcon />}
              strainId={strain.id}
              isStrainPage={isStrainPage}
            />
          )}
          {topThreeTerps.length > 0 && (
            <StrainHighlight
              title="Terpenes"
              hightlights={topThreeTerps}
              icon={() => <TerpenesFlask />}
              strainId={strain.id}
              isStrainPage={isStrainPage}
            />
          )}
        </ul>
        <StrainAttributes className="!pt-0" />
      </div>
    )
  );
};

const StrainHighlight = ({
  hightlights,
  title,
  icon,
  isStrainPage,
  strainId,
}: {
  hightlights: StrainAttribute[] | Terp[];
  title: string;
  icon: () => ReactNode;
  isStrainPage?: boolean;
  strainId?: number;
}) => {
  const { publishEvent } = useEventTracker();

  const Element = useCallback(
    ({ name, i }: { name: string; i: number }) =>
      isStrainPage ? (
        <a
          className={classNames("text-green", {
            "before:content-['•'] before:mx-1 after:content-['•'] after:mx-1 text-nowrap":
              i === 1,
          })}
          key={name}
          href="#strain-sensations-section"
          onClick={() => {
            publishEvent({
              action: Action.click,
              category: "strain highlights",
              label: title.toLowerCase(),
              strainId,
            });
          }}
        >
          {name}
        </a>
      ) : (
        <span
          className={classNames({
            "before:content-['•'] before:mx-1 after:content-['•'] after:mx-1 text-nowrap":
              i === 1,
          })}
          key={name}
        >
          {name}
        </span>
      ),
    [title, strainId, isStrainPage],
  );

  return (
    <li className="flex items-center flex-wrap">
      <span aria-hidden>{icon()}</span>
      <span className="pl-xs font-bold mr-1">{title}:</span>
      {hightlights.map(({ name }, i) => (
        <Element name={name} i={i} key={name} />
      ))}
    </li>
  );
};

export default StrainHighlights;
